import { format, getYear, parse } from "date-fns";
import {
  AccumDateData,
  AccumulationCalculation,
  AccumulationCalculationGroupMath,
  AccumulationCalculationKey,
  AccumulationCalculationMultiple,
  AccumulationCalculationSingle,
  AccumulationsForEmploymentResponse,
} from "../../../employment/employmentApi";
import { AssignmentById } from "../../../talents/talentAPI";

export const isAccumulationCalculationMultiple = (
  value: string | AccumulationCalculation,
): value is AccumulationCalculationMultiple => {
  return (
    typeof value === "object" &&
    value !== null &&
    "ongoingAverages" in value &&
    value.ongoingAverages !== undefined &&
    typeof value.ongoingAverages === "object"
  );
};

export const isAccumulationCalculationSingle = (
  value: string | AccumulationCalculation,
): value is AccumulationCalculationSingle => {
  return typeof value === "object" && value !== null && "partitionKey" in value;
};

export const isAccumulationCalculationGroupMath = (
  value: string | AccumulationCalculation,
): value is AccumulationCalculationGroupMath => {
  return (
    typeof value === "object" &&
    value !== null &&
    ("ongoingAverages" in value || "partitions" in value) &&
    !("partitionKey" in value) // Distinguish from single calculation
  );
};

export const accumulationCalculationKeys = [
  "lomakeskiansiolaskenta",
  "keskiansiolaskenta",
  "työajanlyhennyslaskenta",
  "vuosivapaalaskenta",
] as const;

export const isAccumulationCalculationKey = (
  key: string,
): key is AccumulationCalculationKey =>
  accumulationCalculationKeys.includes(key as AccumulationCalculationKey);

export const getAssignmentName = (
  translation: Record<string, string>,
  selectedAssignment?: AssignmentById,
) => {
  if (selectedAssignment) {
    const start = format(selectedAssignment?.details.start, "dd.MM.yyyy");
    const workLocationName = selectedAssignment.details.workLocationName;
    const workTitleId = selectedAssignment.details.workTitleId;
    const workTitleName = translation[workTitleId] || workTitleId;
    return `${start} - ${workLocationName} - ${workTitleName}`;
  }

  return null;
};

export const formatPartitionKeyDisplayValue = (value?: string) => {
  if (!value) return "";
  // value = e.g. 2024-12-31 2024-01-01 2024-12-31
  // [valueDate] [startDate] [endDate]
  // render only year of valueDate
  const keyDate = value.split(" ")[0];
  const parsedDate = parse(keyDate, "yyyy-MM-dd", new Date());
  const year = getYear(parsedDate);
  return year;
};

// Helper function to combine multiple accumulation responses for GROUP_MATH
export const combineGroupMathAccumulations = (
  accumulations: AccumulationsForEmploymentResponse[],
  stateNames: string[],
): AccumulationsForEmploymentResponse => {
  if (accumulations.length === 0) {
    return { meta: { keys: [], stateNames: [] }, data: [] };
  }

  // lomakeskiansiolaskenta is the primary state
  const primaryStateIndex = stateNames.findIndex((name) =>
    name.includes("lomakeskiansiolaskenta"),
  );

  if (primaryStateIndex === -1) {
    // This should never happen for properly configured GROUP_MATH accumulations
    console.error("No lomakeskiansiolaskenta found in GROUP_MATH accumulation");
    return { meta: { keys: [], stateNames: [] }, data: [] };
  }

  const primaryAccumulation = accumulations[primaryStateIndex];
  const secondaryAccumulations = accumulations.filter(
    (_, index) => index !== primaryStateIndex,
  );

  // Start with primary data structure
  const combinedData: AccumDateData[] = primaryAccumulation.data.map(
    (dateEntry) => {
      const date = dateEntry.date;

      // For each row in primary data
      const combinedRows = dateEntry.rows.map((primaryRow, rowIndex) => {
        const combined = { ...primaryRow };

        // Try to find matching secondary data for this date
        secondaryAccumulations.forEach((secondaryAccum, secIndex) => {
          const secondaryDateEntry = secondaryAccum.data.find(
            (entry) => entry.date === date,
          );

          if (secondaryDateEntry && secondaryDateEntry.rows.length > 0) {
            // Find matching secondary row by deltaId or assignmentId
            let secondaryRow = secondaryDateEntry.rows.find(
              (row) =>
                (primaryRow.deltaId && row.deltaId === primaryRow.deltaId) ||
                (primaryRow.assignmentId &&
                  row.assignmentId &&
                  row.assignmentId === primaryRow.assignmentId),
            );

            // Fallback: if only one secondary row, use it
            if (!secondaryRow && secondaryDateEntry.rows.length === 1) {
              secondaryRow = secondaryDateEntry.rows[0];
            }

            // Last resort: if no match found, use last row (most recent)
            // This is better than first row when multiple deltas exist for same date
            if (!secondaryRow) {
              const lastIndex = secondaryDateEntry.rows.length - 1;
              secondaryRow = secondaryDateEntry.rows[lastIndex];
            }

            Object.keys(secondaryRow).forEach((key) => {
              if (key !== "deltaId") {
                let value = secondaryRow![key];

                // Parse JSON strings
                if (
                  typeof value === "string" &&
                  key === "viikkokeskityömäärälaskenta"
                ) {
                  try {
                    value = JSON.parse(value);
                  } catch (e) {
                    // Keep as string if parsing fails
                  }
                }

                combined[key] = value;
              }
            });

            // Update deltaId to secondary's if we merged data
            if (secondaryRow!.deltaId) {
              combined.deltaId = secondaryRow!.deltaId;
            }
          }
        });

        return combined;
      });

      return {
        date: dateEntry.date,
        rows: combinedRows,
      };
    },
  );

  // Combine keys and stateNames from all accumulations
  const combinedKeys = Array.from(
    new Set(accumulations.flatMap((acc) => acc.meta.keys)),
  );

  return {
    meta: {
      keys: combinedKeys,
      stateNames: [stateNames[primaryStateIndex]], // Only primary state name
    },
    data: combinedData,
  };
};

export const shouldIncludeAssignmentId = (scope: string): boolean => {
  return scope.includes("ASSIGNMENT");
};
